import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, finalize, from, switchMap, take } from 'rxjs';
import { SapSubmittalResultDialogComponent } from '../Components/dialogs/common/sap-submittal/sap-submittal-result-dialog/sap-submittal-result-dialog.component';
import { ISapSubmittalResultDialogData } from '../models/common/ISapSubmittalResultDialogData';
import { SapSubmittalErrorsDialogComponent } from '../Components/dialogs/common/sap-submittal/sap-submittal-errors-dialog/sap-submittal-errors-dialog.component';
import { ISapSubmittalErrorsDialogData } from '../models/common/ISapSubmittalErrorsDialogData';
import { ISapSubmittalValidationResult } from '../models/common/ISapSubmittalValidationResult';
import { CommonDataService } from './common-data.service';
import { CommonFunctionsService } from './common-functions.service';
import { ReportsCommonService } from './reports/reports-common/reports-common.service';
import { EstimateSaveService } from './estimate-save.service';
import { SapSubmittalApiService, SapSubmittalEvent } from './api/sap-submittal-api.service';
import { IndexDBService } from './common/indexDB.service';
import { UserProfileService } from './common/user-profile.service';
import { SapSubmittalRequest } from '../models/dtos/Classes/SapSubmittalRequest';
import { SapSubmittalResponse } from '../models/dtos/Classes/SapSubmittalResponse';
import { DocumentUploadModel } from '../models/dtos/Classes/DocumentUploadModel';
import { Material } from '../models/dtos/Classes/Material';
import { SubSystem } from '../models/dtos/Classes/SubSystem';
import { MaterialLevelwiseData } from '../models/dtos/Classes/MaterialLevelwiseData';
import { SQWebProject } from '../models/dtos/Classes/SQWebProject';
import { SystemChildren } from '../models/dtos/Classes/SystemChildren';
import { IAreaChild } from '../models/dtos/Interfaces/IAreaChild';
import {
  SapSubmittalPrerequisiteRequest,
  SapSubmittalPrerequisitesResponse,
  SummaryEstimateParameter,
  OpenEstimateRequest,
  OpenEstimateResponse,
  UnlockSapSubmittalRequest,
  CostReviewStatus,
  EstimateStatus,
  EstimateTab,
  SQWebArea,
  SQWebSystem,
  ReportList,
  ForcePublishDetails,
  CollaborationHistoryEnum,
  CollaborationState
} from '../models/dtos';
import { CONSTANTS } from '../Constants/Constants';
import { SapSubmittalRequiredFieldDialogComponent } from '../Components/dialogs/common/sap-submittal/sap-submittal-required-field-dialog/sap-submittal-required-field-dialog.component';
import { PdfBufferService } from './reports/pdf-buffer/pdf-buffer.service';
import { GocCollaborationRestrictionHandlerService } from './common/goc-collaboration-restriction-handler.service';
import { LoadingSpinnerService } from './common/loading-spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { OpenEstimateOperationsService } from './common/open-estimate-operations.service';
import { EstimateCloseService } from './estimate-close.service';

// Spinner messages shown while the SAP submittal chain runs its long client-side phases.
// Once the request is in flight the server takes over via SSE status events.
const SPINNER_MESSAGE_CREATING_SAVE_REQUEST = 'Creating save request';
const SPINNER_MESSAGE_CREATING_DOCUMENTS = 'Creating documents for upload';
const SPINNER_MESSAGE_AWAITING_RESPONSE = 'Awaiting response';

// Summary estimate parameter keys matching DB values from WSSP_GetSummaryEstimateData
const SUMMARY_PARAM_SYSTEM = 'SummaryEstimateSystem';
const SUMMARY_PARAM_USER_DEFINED_PARTS = 'UserDefinedParts';
const SUMMARY_PARAM_SUBCONTRACTORS = 'Subcontractors';
const SUMMARY_PARAM_MISCELLANEOUS = 'Miscellaneous';
const SUMMARY_PARAM_SELL_PRICE = 'SellPrice';
const SUMMARY_PARAM_LABOR_TASKS = 'LaborTasks';

// US culture ID (maps to desktop PropertyConstants.ONE / UserPreferencesValues.CultureID)
const US_CULTURE_ID = 1;

/**
 * Unified service for SAP submittal operations: prerequisite validation and submission.
 *
 * Validation:
 *   Combines DB-dependent checks (via GetSapSubmittalPreRequisites API) with
 *   locally available project data from CommonDataService.
 *
 *   Desktop equivalents in MenuViewModel.cs / OnSAPSubmittalCommandExecute:
 *     1. GOC restriction           → handled server-side (API throws UnauthorizedException)
 *     2. Collaborative checkout    → handled server-side (API throws ConflictException)
 *     3. ProposalErrorGeneration() → checkProposalError()
 *     4. CostReviewErrorGeneration() → checkCostReviewError()
 *     5. OpportunityErrorGeneration() → checkOpportunityError()
 *     6. SummaryEstimateError()    → checkSummaryEstimateError()
 *     7. Sales office/segment      → checkSalesOfficeInvalid() / checkSegmentInvalid()
 *     8. Negative margin           → checkNegativeMargin()
 *
 * Submission:
 *   Builds the full SapSubmittalRequest from available data and submits to the backend.
 *   The backend handles XML generation, upload, estimate save, document upload, and GIL integration.
 *
 * Unlock (Resubmittal):
 *   Creates a snapshot clone of the submitted estimate and resets the master to Active status.
 *   Desktop equivalent: OnSAPReSubmittalCommandExecute() in MenuViewModel.cs
 */
@Injectable({ providedIn: 'root' })
export class SapSubmittalService {

  /** Emits true when an async operation is in progress, false when idle */

  constructor(
    private readonly dialog: MatDialog,
    private readonly commonDataService: CommonDataService,
    private readonly commonFunctionsService: CommonFunctionsService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly sapSubmittalApiService: SapSubmittalApiService,
    private readonly indexDbService: IndexDBService,
    private readonly userProfileService: UserProfileService,
    private readonly pdfBufferService: PdfBufferService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly translate: TranslateService,
    private readonly openEstimateOperationsService: OpenEstimateOperationsService,
    private readonly estimateCloseService: EstimateCloseService
  ) { }

  // ═══════════════════════════════════════════════════════════════════════════
  // ORCHESTRATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Executes the full SAP submittal flow: validate → warnings → blocking checks → submit → update state.
   * Manages all dialog interactions internally. The component only needs to subscribe to loading$
   * and call this method.
   *
   * @param onClose Optional callback invoked when the request dialog should close
   *                (e.g. on successful submission or API error).
   */
  executeFullSubmittalFlow(onClose?: () => void): void {

    this.validatePrerequisites().subscribe({
      next: (result) => {
        this.processValidationResult(result, onClose);
      },
      error: async (error) => {
        if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
          return;
        }
        onClose?.();
      }
    });
  }

  /**
   * Executes the full unlock (resubmittal) flow:
   *   1. Build UnlockSapSubmittalRequest from local data
   *   2. Call backend API (validates GOC, collaborative state, creates clone, resets status to Active)
  *   3. On success: close the previous IndexedDB context and reopen the fresh estimate response
   *
   * Desktop equivalent: OnSAPReSubmittalCommandExecute() in MenuViewModel.cs
   * The web backend consolidates the desktop's multi-step flow (MakeCloneForReSubmittal,
   * OpenEstimate, Save, ForcePublish) into a single API call.
   *
   * @param reSubmittalNotes Optional reason for unlocking entered by the user (max 256 chars); may be empty
   * @param onClose Optional callback invoked when the dialog should close (on success only;
   *                on error the dialog stays open so the user can cancel or retry)
   */
  executeUnlockFlow(reSubmittalNotes: string, onClose?: () => void): void {

    const request = this.buildUnlockRequest(reSubmittalNotes);
    this.sapSubmittalApiService.unlockSubmittal(request).subscribe({
      next: async (response: OpenEstimateResponse) => {
        onClose?.();
        try {
          await this.estimateCloseService.closeEstimate();
          await this.openEstimateOperationsService.openEstimate(response);
        } catch (error) {
          console.error('Failed to reopen estimate after unlock', error);
        }
      },
      error: async (error) => {
        if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
          return;
        }
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Performs all pre-submission validation checks for SAP submittal.
   * Calls the backend prerequisites API and combines with local project data checks.
   *
   * @returns Observable that emits the complete validation result.
   */
  validatePrerequisites(): Observable<ISapSubmittalValidationResult> {
    const request = this.buildPrerequisiteRequest();
    return this.sapSubmittalApiService.getPrerequisites(request).pipe(
      switchMap(response => from(this.evaluateAllChecks(response)))
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBMISSION
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Builds the full SapSubmittalRequest from available data and submits the estimate to SAP.
   * The backend handles XML generation, upload, estimate save, document upload, and GIL integration,
   * streaming a status event before each of those steps.
   *
   * @param isQuotedServiceEstimate Optional flag from prerequisites response. If not provided, defaults to false.
   */
  submitEstimate(isQuotedServiceEstimate?: boolean): Observable<SapSubmittalEvent> {
    return from(this.buildRequest(isQuotedServiceEstimate)).pipe(
      switchMap(request => {
        this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_AWAITING_RESPONSE);
        return this.sapSubmittalApiService.submitEstimate(request);
      })
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ORCHESTRATION — PRIVATE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Processes the validation result.
   * If pre-submit warnings exist, shows the consolidated errors dialog (desktop: ConsolidateErrorMessage).
   * If no warnings, proceeds directly to blocking error checks.
   */
  private processValidationResult(result: ISapSubmittalValidationResult, onClose?: () => void): void {
    const prerequisitesResponse = result.prerequisitesResponse;
    if (result.hasPreSubmitWarnings) {
      const dialogData: ISapSubmittalErrorsDialogData = {
        isProposalError: result.isProposalError,
        isCostReviewError: result.isCostReviewError,
        isOpportunityError: result.isOpportunityError,
        isSummaryEstimateError: result.isSummaryEstimateError,
        isBlockingMode: (result.isCostReviewError && prerequisitesResponse.isCostReviewMandate) ||
                        (result.isOpportunityError && prerequisitesResponse.isOpportunityMandate) ||
                        (result.isProposalError && prerequisitesResponse.isProposalMandate) ||
                        (result.isSummaryEstimateError && prerequisitesResponse.isSummaryEstimateMandate),
      };

      const warningsDialog = this.dialog.open(SapSubmittalErrorsDialogComponent, {
        width: '500px',
        data: dialogData,
        disableClose: true
      });

      warningsDialog.afterClosed().pipe(take(1)).subscribe((shouldContinue: boolean) => {
        if (shouldContinue) {
          this.checkBlockingErrorsAndProceed(result, onClose);
        }
      });
    } else {
      this.checkBlockingErrorsAndProceed(result, onClose);
    }
  }

  /**
   * Checks for blocking errors that prevent submission entirely.
   * Desktop equivalents:
   *   - InvalidSalesOfficeMsgDialog (IsSalesOfficeInvalid || IsSegmentInvalid)
   *   - SAPNegativeMarginMsgDialog (CultureID==1 && SoldASGMPercent < 0)
   */
  private checkBlockingErrorsAndProceed(result: ISapSubmittalValidationResult, onClose?: () => void): void {
    if (result.isSalesOfficeInvalid || result.isSegmentInvalid) {
      const blockingData: ISapSubmittalErrorsDialogData = {
        isSalesOfficeInvalid: result.isSalesOfficeInvalid,
        isSegmentInvalid: result.isSegmentInvalid,
        isBlockingMode: true
      };
      this.dialog.open(SapSubmittalErrorsDialogComponent, { width: '450px', data: blockingData, disableClose: true });
      return;
    }

    if (result.isNegativeMargin) {
      const blockingData: ISapSubmittalErrorsDialogData = {
        isNegativeMargin: true,
        isBlockingMode: true
      };
      this.dialog.open(SapSubmittalErrorsDialogComponent, { width: '450px', data: blockingData, disableClose: true });
      return;
    }

    this.proceedWithSubmission(result.isQuotedServiceEstimate, onClose);
  }

  /**
   * All prerequisite checks passed. Calls the SAP submission API.
   * Shows loading state while waiting, then opens the result dialog with the API response.
   *
   * Desktop equivalent: SapSubmittalValidityMessage() → CreateXMLDocument() → SaveEstimateDataForSAPSubmittal()
   */

  private doSubmit(isQuotedServiceEstimate: boolean, onClose?: () => void): void {
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);

    this.submitEstimate(isQuotedServiceEstimate).pipe(
      finalize(() => {
        this.loadingSpinnerService.updateMessage('');
        this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
      })
    ).subscribe({
      next: async (event: SapSubmittalEvent) => {
        if (event.type === 'status') {
          this.loadingSpinnerService.updateMessage(event.status ?? '');
          return;
        }

        const response = event.result!;
        onClose?.();

        const resultData: ISapSubmittalResultDialogData = {
          isSuccess: response.isSapSubmittalSuccessfull,
          response
        };
        const resultDialog = this.dialog.open(SapSubmittalResultDialogComponent, {
          width: '500px',
          data: resultData,
          disableClose: true
        });
        resultDialog.afterClosed().pipe(take(1)).subscribe(() => {
          if (response.isSapSubmittalSuccessfull) {
            const estimateData = this.commonDataService.getEstimateData();
            if (estimateData) {
              estimateData.estimateStatusId = EstimateStatus.Submitted;
              this.commonDataService.publishProjectContainer(response.project);
              this.commonDataService.isSapSubmitted = true;
              this.commonDataService.isSaveEnabled = false;
              if (this.commonDataService.isCollaborationEstimate() && response.collaborationHubData) {
                this.commonFunctionsService.updateCollaborationHubData(response.collaborationHubData);
                this.commonDataService.setCollaborationState(CollaborationState.Open);
              }
            }
            this.commonDataService.enableReadOnlyMode(true);
          }
        });
      },
      error: async (error) => {
        await this.handleSubmitFailure(error, onClose);
      }
    });
  }

  /**
   * GOC restriction failures are handled here because they can trigger
   * collaboration-specific discard/close workflow instead of generic error UI.
   */
  private async handleSubmitFailure(error: unknown, onClose?: () => void): Promise<void> {
    if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
      return;
    }

    onClose?.();
  }

  private proceedWithSubmission(isQuotedServiceEstimate: boolean, onClose?: () => void): void {
    const userPreferences= this.commonDataService.getPreferenceData();

    const salesPersonEmail = userPreferences?.preferences.salesPersonEmail;
    const salesPersonName = userPreferences?.preferences.salesPersonName;
    
    if(!salesPersonEmail || salesPersonEmail?.length === 0 || !salesPersonName || salesPersonName?.length === 0){
      const dialogRef = this.dialog.open(SapSubmittalRequiredFieldDialogComponent, {
            width: '500px',
            disableClose: true
          });
          dialogRef.afterClosed().pipe(take(1)).subscribe(result =>  {
            if(result){
              this.commonDataService.setPreferenceData({
                ...userPreferences,
                preferences: {
                  ...userPreferences?.preferences,
                  salesPersonEmail: result.email,
                  salesPersonName: result.name
                }
              });
              this.doSubmit(isQuotedServiceEstimate, onClose);
            }
          });
    }
    else {
      this.doSubmit(isQuotedServiceEstimate, onClose);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION — PRIVATE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Builds the prerequisite request from locally available data.
   * Maps data from CommonDataService to SapSubmittalPrerequisiteRequest.
   */
  private buildPrerequisiteRequest(): SapSubmittalPrerequisiteRequest {
    const estimateData = this.commonDataService.getEstimateData();
    const estimateMetaData = this.commonDataService.getEstimateMetaData();

    const request = new SapSubmittalPrerequisiteRequest();
    request.estimateId = this.commonDataService.getEstimateId();
    request.bsetUserId = this.commonDataService.getUserId();
    request.userEmailId = this.userProfileService.userInfo?.mail ?? '';
    request.segmentId = estimateData?.profitCenterId ?? 0;
    request.isServiceAgreement = false; // SOL estimates are non-SA
    request.subsegmentId = estimateMetaData?.estimateSettings?.subSegmentId ?? 0;
    request.cultureId = this.commonDataService.getEstimateCultureId();
    request.isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate();

    return request;
  }

  /**
   * Evaluates all validity checks using the API response combined with local data.
   * This mirrors the checks in OnSAPSubmittalCommandExecute before SapSubmittalValidityMessage().
   */
  private async evaluateAllChecks(response: SapSubmittalPrerequisitesResponse): Promise<ISapSubmittalValidationResult> {
    // Pre-submit warnings (desktop: ConsolidateErrorMessage dialog - user can continue)
    const isProposalError = this.checkProposalError(response);
    const isCostReviewError = this.checkCostReviewError(response);
    const isOpportunityError = this.checkOpportunityError();
    const isSummaryEstimateError = await this.checkSummaryEstimateError(response);

    // Blocking errors (desktop: separate dialogs that prevent submission)
    const isSalesOfficeInvalid = this.checkSalesOfficeInvalid();
    const isSegmentInvalid = this.checkSegmentInvalid();
    const isNegativeMargin = this.checkNegativeMargin();

    const hasPreSubmitWarnings = isProposalError || isCostReviewError || isOpportunityError || isSummaryEstimateError;
    const hasBlockingErrors = isSalesOfficeInvalid || isSegmentInvalid || isNegativeMargin;

    return {
      isProposalError,
      isCostReviewError,
      isOpportunityError,
      isSummaryEstimateError,
      hasPreSubmitWarnings,
      isSalesOfficeInvalid,
      isSegmentInvalid,
      isNegativeMargin,
      hasBlockingErrors,
      isQuotedServiceEstimate: response?.isQuotedServiceEstimate ?? false,
      prerequisitesResponse: response
    };
  }

  /**
   * Check 1: Proposal Error
   *
   * Desktop equivalent: ProposalErrorGeneration() in MenuViewModel.cs
   * Returns true if no proposal ID is associated with this estimate.
   * Checks both the DB value (API response) and the local estimate data.
   */
  private checkProposalError(response: SapSubmittalPrerequisitesResponse): boolean {
    // Primary check: proposal ID from the DB via stored procedure
    if (response?.proposalId && response.proposalId.trim().length > 0) {
      return false;
    }

    // Fallback: check from local estimate data (matches desktop GetProposalId fallback)
    const estimateData = this.commonDataService.getEstimateData();
    return !estimateData?.proposalID || estimateData.proposalID.trim().length === 0;
  }

  /**
   * Check 2: Cost Review Error
   *
   * Desktop equivalent: CostReviewErrorGeneration() in MenuViewModel.cs
   * Returns true if SAP submittal is restricted due to unapproved cost review.
   *
   * Desktop logic:
   *   isCostReviewError = IsSapSubmittalRestricted && IsReviewTabVisible
   *   isCostReviewError = isCostReviewError && !IsOptionalGroupboxCheckBox
   *                       && CostReviewDetails.Status != Approved
   */
  private checkCostReviewError(response: SapSubmittalPrerequisitesResponse): boolean {
    // If the user is not restricted from SAP submittal, no error
    if (!response?.isSapSubmittalRestricted) {
      return false;
    }
    // If Cost Review is not required / Review tab is not visible for this estimate, no error
    if (!this.commonDataService.getReviewTabVisibility()) {
      return false;
    }

    // Check if cost review is applicable (equivalent to IsReviewTabVisible)
    const costReviewData = this.commonDataService.getCostReviewData();
    const costReviewStatus = costReviewData?.status;

    // If no cost review data or status is null, cost review is not visible/applicable
    if (costReviewStatus == null) {
      return false;
    }

    // Cost review is not an error if status is Approved
    if (costReviewStatus === CostReviewStatus.Approved) {
      return false;
    }

    // ToDo: Check if cost review is optional (desktop: IsOptionalGroupboxCheckBox)
    // For now, treat all restricted + non-approved cost reviews as errors.
    // When the optional cost review workflow is implemented on the web, add:
    //   if (isCostReviewOptional) return false;

    return true;
  }

  /**
   * Check 3: Opportunity Error
   *
   * Desktop equivalent: OpportunityErrorGeneration() in MenuViewModel.cs
   * Returns true if no opportunity is linked to the estimate.
   * Checks Project.ProjectInstance.OpportunityId (mapped to EstimateSettings.opportunityId)
   */
  private checkOpportunityError(): boolean {
    // Check from estimate settings (mapped from Project.OpportunityId via SQDesktopToSQWebMappingsHelper)
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    if (estimateSettings?.opportunityId && estimateSettings.opportunityId.trim().length > 0) {
      return false;
    }
    return true;
  }

  /**
   * Check 4: Summary Estimate Error
   *
   * Desktop equivalent: SummaryEstimateError() in MenuViewModel.cs
   * Returns true if the estimate appears to be a "summary estimate" - too few items
   * for its sell price. This is a WARNING, not a blocking error.
   *
   * Desktop logic traverses Project.Areas, counts systems, subcontractors, misc items,
   * labor tasks, and materials, comparing against thresholds from the DB
   * (via WSSP_GetSummaryEstimateData stored proc, cached as SummaryEstimateParameters).
   *
   * A summary estimate is flagged when:
   *   - Sell price > threshold
   *   - NOT a subcontractor estimate (sell price > threshold AND has subcontractors)
   *   - NOT a labor estimate (sell price > threshold AND has labor tasks)
   *   - Misc count within threshold
   *   - System count within threshold
   *   - Material count (user-defined parts) within threshold AND no standard equipment present
   */
  private async checkSummaryEstimateError(response: SapSubmittalPrerequisitesResponse): Promise<boolean> {
    const params = response?.summaryEstimateParameters;
    if (!params || params.length === 0) {
      return false;
    }

    const thresholds = this.parseSummaryEstimateParameters(params);
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    const sqWebProject = this.commonDataService.getSQWebProject();

    if (!estimateTab || !sqWebProject) {
      return false;
    }

    const sellPrice = estimateTab.soldASSellPrice ?? 0;

    // If sell price doesn't exceed the threshold, no summary estimate concern
    if (sellPrice <= thresholds.sellPrice) {
      return false;
    }

    // Check for subcontractor estimate: sell price > threshold AND has subcontractors
    const hasSubcontractors = this.hasSubcontractorCosts(sqWebProject);
    if (hasSubcontractors) {
      return false; // Subcontractor estimate, not a summary estimate
    }

    // Check for labor estimate: sell price > threshold AND has labor tasks
    const hasLaborTasks = this.hasNonZeroLaborHours(estimateTab);
    if (hasLaborTasks) {
      return false; // Labor estimate, not a summary estimate
    }

    // Count visible systems across all areas
    let systemCount = 0;
    if (sqWebProject.areas) {
      for (const area of sqWebProject.areas) {
        systemCount += this.countVisibleSystems(area);
      }
    }

    // If system count exceeds threshold, not a summary estimate
    if (systemCount > thresholds.system) {
      return false;
    }

    // Material/parts count and standard equipment check
    // Desktop: GetCountFromProjectComponent() counts materials where Guid is non-empty OR ReferenceNumber == USER_DEFINED_RPN
    // Desktop: isSummaryEstimateStandardEquipment is set to true if ANY material doesn't match the above criteria
    // Final check: materialCount <= UserDefinedParts threshold AND !isSummaryEstimateStandardEquipment
    const materialLevelwiseData = await this.indexDbService.getAllMaterialData();
    const { materialCount, hasStandardEquipment } = this.countMaterialsAndCheckStandardEquipment(materialLevelwiseData);

    // Desktop: if (materialCount <= UserDefinedParts && !isSummaryEstimateStandardEquipment) → isSummaryEstimateError = true
    if (materialCount <= thresholds.userDefinedParts && !hasStandardEquipment) {
      return true; // Summary estimate - flag warning
    }

    return false;
  }

  /**
   * Check 5: Sales Office Invalid
   *
   * Desktop equivalent: EstimateProperties.IsSalesOfficeInvalid
   * This flag is set during estimate open when the sales office doesn't exist
   * in the current database. In the web app, a missing/zero salesOfficeId indicates invalidity.
   */
  private checkSalesOfficeInvalid(): boolean {
    const estimateData = this.commonDataService.getEstimateData();
    return !estimateData?.salesOfficeId || estimateData.salesOfficeId === 0;
  }

  /**
   * Check 6: Segment Invalid
   *
   * Desktop equivalent: EstimateProperties.IsSegmentInvalid
   * Checks if the estimate's segment/business unit is valid.
   */
  private checkSegmentInvalid(): boolean {
    const estimateData = this.commonDataService.getEstimateData();
    return !estimateData?.businessUnitID || estimateData.businessUnitID === 0;
  }

  /**
   * Check 7: Negative Margin (US only)
   *
   * Desktop equivalent: UserPreferencesValues.CultureID == PropertyConstants.ONE
   *                     && SoldASGMPercent < PropertyConstants.ZERO_POINT_ZERO
   *
   * Only applies to US estimates (CultureID = 1). Checks if gross margin percentage
   * is negative, which requires special approval before SAP submission.
   */
  private checkNegativeMargin(): boolean {
    const cultureId = this.commonDataService.getEstimateCultureId();

    // Only check for US estimates
    if (cultureId !== US_CULTURE_ID) {
      return false;
    }

    const estimateData = this.commonDataService.getEstimateData();
    return estimateData?.marginPercent != null && estimateData.marginPercent < 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UNLOCK — PRIVATE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Builds the UnlockSapSubmittalRequest from locally available data.
   *
   * Desktop equivalent: Parameters passed to MakeCloneForReSubmittal() and
   * collaborative estimate checks in OnSAPReSubmittalCommandExecute().
   */
  private buildUnlockRequest(reSubmittalNotes: string): UnlockSapSubmittalRequest {
    const request = new UnlockSapSubmittalRequest();
    request.estimateId = this.commonDataService.getEstimateId();
    request.bsetUserId = this.commonDataService.getUserId();
    request.userEmailId = this.userProfileService.userInfo?.mail ?? '';
    request.isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate();
    request.reSubmittalNotes = reSubmittalNotes;
    request.forcePublishDetails = this.getForcePublishDetailsForUnlock();
    const openEstimateRequest = new OpenEstimateRequest();
    openEstimateRequest.userId = request.bsetUserId;
    openEstimateRequest.estimateId = request.estimateId;
    openEstimateRequest.userEmailId = request.userEmailId;
    openEstimateRequest.culture = this.commonDataService.getCurrentApplicationCulture();
    openEstimateRequest.isArchive = this.commonDataService.isArchivedEstimate();
    request.openEstimateRequest = openEstimateRequest;
    return request;
  }

  private getForcePublishDetailsForUnlock(): ForcePublishDetails {
    return {
      forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_SOL_WHILE_SAP_RESUBMISSION,
      operation: CollaborationHistoryEnum.SAPSubmittalUnlock,
      modificationNotes: this.translate.instant('MODIFICATIONNOTES_UNLOCK'),
      estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_SOL_ON_SAP_RESUBMISSION,
      isEstimateBeingCheckedOutAfterPublish: true
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SUBMISSION — PRIVATE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Constructs the complete SapSubmittalRequest from CommonDataService, preferences, and IndexedDB data.
   */
  private async buildRequest(isQuotedServiceEstimate?: boolean): Promise<SapSubmittalRequest> {
    const estimateData = this.commonDataService.getEstimateData();
    const estimateMetaData = this.commonDataService.getEstimateMetaData();
    const estimateSettings = estimateMetaData?.estimateSettings;
    const preferences = this.commonDataService.getPreferenceData()?.preferences;
    const cultureId = this.commonDataService.getEstimateCultureId();

    this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_CREATING_SAVE_REQUEST);
    const estimateSaveRequest = await this.estimateSaveService.createSaveEstimateRequest();
    const businessChar = this.commonDataService.getBusiness();

    estimateSaveRequest.isCollaborativeEstimate = this.commonDataService.isCollaborationEstimate();
    estimateSaveRequest.userEmailId = this.userProfileService.userInfo.mail;

    const request = new SapSubmittalRequest();
    request.bsetUserId = this.commonDataService.getUserId();
    request.cultureId = cultureId;
    
    request.salesPersonEmail = preferences?.salesPersonEmail ?? '';

    // fileName is constructed on the backend from branchName, estimateId, and email prefix — empty string is acceptable
    request.fileName = '';
    request.country = cultureId === 1 ? 'US' : 'CA';
    request.segmentId = estimateData.profitCenterId;
    request.precision = this.commonDataService.getDecimalPrecision() ?? 2;
    request.isProductPromotionEnabled = true;
    request.isCUIEstimate = estimateSettings?.isCUI ?? false;
    request.showPPM = preferences?.showPPM ?? false;
    request.isQuotedServiceEstimate = isQuotedServiceEstimate ?? false;
    request.userName = preferences?.salesPersonName ?? '';
    request.startDate = estimateData?.startDate ? new Date(estimateData.startDate) : new Date();
    request.endDate = estimateData?.endDate ? new Date(estimateData.endDate) : new Date();
    request.business = this.commonFunctionsService.getBusinessType(businessChar);
    request.estimateSaveRequest = estimateSaveRequest;

    this.loadingSpinnerService.updateMessage(SPINNER_MESSAGE_CREATING_DOCUMENTS);
    request.costReviewDoc = await this.getCostReviewDocUploadModel();
    request.sapSubmittalDoc = await this.getSubmittalPackageDocUploadModel();

    const estimateTab = estimateMetaData?.estimateTab;
    const isPartsAndSmarts = estimateSettings?.installTypeID === 2;
    request.partsAndSmartsHardwareComponent = isPartsAndSmarts
      ? this.calculatePSHardwareComponent(estimateTab, request.precision)
      : 0;
    request.partsAndSmartsConsultingLabor = isPartsAndSmarts
      ? this.calculatePSConsultingLabor(estimateTab)
      : 0;
    request.forcePublishDetails = this.getForcePublishDetailsForLock();
    return request;
  }

  private getForcePublishDetailsForLock() : ForcePublishDetails {
    return {
      forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_WHILE_SAP_SUBMISSION,
      operation: CollaborationHistoryEnum.SAPSubmitted,
      modificationNotes: this.translate.instant('SAP_SUBMIT_SYSTEM_PUBLISH'),
      estimateCheckoutAdditionalInfo: CONSTANTS.EMPTY_STRING,
      isEstimateBeingCheckedOutAfterPublish: true
    }
  }
  /**
   * Calculate Parts & Smarts Hardware Component cost.
   *
   * Desktop equivalent: CalcuatePSHardwareComponent() in SAPSubmittalXMLCreationViewModel.cs
   *
   * Desktop logic (when partsAndSmartsFlag == true):
   *   siemensMaterialTotalSellPrice = MaterialCostVM.SiemensMaterialSellPrice
   *                                 + MaterialCostVM.SiemensMaterialWarrantySellPrice
   *                                 + MaterialCostVM.SiemensMaterialShippingHandlingSellPrice
   *   siemensOutsideMaterialTotalSellPrice = MaterialCostVM.OutsideMaterialSellPrice
   *                                        + MaterialCostVM.OutsideMaterialShippingHandlingSellPrice
   *                                        + MaterialCostVM.OutsideMaterialWarrantySellPrice
   *   totalHardwareComponent = siemensMaterialTotalSellPrice + siemensOutsideMaterialTotalSellPrice
   *
   *   Then a penny-difference adjustment: if rounded totalHardwareComponent != EstimateTabSellPriceVM.MaterialSellPrice
   *   AND ElectricalMaterialSellPrice == 0 AND MechanicalMaterialSellPrice == 0:
   *     adjust totalHardwareComponent by the difference (if abs(difference) < 1)
   *
   * ToDo : SAP_SUBMITTAL — The desktop MaterialCostVM aggregates individual Siemens/Outside material sell prices
   * which are derived from costs + GM%. The web EstimateTab has `materialCostSellPrice` as the total material
   * sell price. When individual material sell price breakdowns (SiemensMaterialSellPrice, SiemensMaterialWarrantySellPrice,
   * SiemensMaterialShippingHandlingSellPrice, OutsideMaterialSellPrice, OutsideMaterialShippingHandlingSellPrice,
   * OutsideMaterialWarrantySellPrice) become available in the web model, implement the full calculation.
   * For now, uses the aggregated materialCostSellPrice from EstimateTab as the hardware component.
   *
   * @param estimateTab The estimate tab data
   * @param precision The decimal precision for rounding
   * @returns The total hardware component sell price
   */
  private calculatePSHardwareComponent(estimateTab: EstimateTab | undefined, precision: number): number {
    if (!estimateTab) {
      return 0;
    }

    // ToDo : SAP_SUBMITTAL — Replace with granular material sell prices when available:
    //   const siemensMaterialTotal = siemensMaterialSellPrice + siemensMaterialWarrantySellPrice + siemensMaterialSHSellPrice;
    //   const outsideMaterialTotal = outsideMaterialSellPrice + outsideMaterialWarrantySellPrice + outsideMaterialSHSellPrice;
    //   let hardware = siemensMaterialTotal + outsideMaterialTotal;
    //   // Penny adjustment: if (rounded(hardware) !== estimateTab.materialCostSellPrice
    //   //   && elecMaterialSellPrice === 0 && mechMaterialSellPrice === 0)
    //   //   adjust hardware by the difference (if abs(diff) < 1)
    return estimateTab.materialCostSellPrice ?? 0;
  }

  /**
   * Calculate Parts & Smarts Consulting Labor cost.
   *
   * Desktop equivalent: CalcuatePSConsultingLabor() in SAPSubmittalXMLCreationViewModel.cs
   *
   * Desktop logic (when partsAndSmartsFlag == true):
   *   totalConsultingLabor = LaborCostVM.LaborCostSellPrice
   *                        + MiscellaneousTravelCostVM.MiscellaneousCostSellPrice
   *                        + MiscellaneousTravelCostVM.SubcontractorFeeCostSellPrice
   *                        + MiscellaneousTravelCostVM.MechFeeCostSellPrice
   *                        + SubcontractorCostVM.SubContractorCostSellPrice
   *                        + TechnologyFeeCostVM.TechnologyFeeSellPrice
   *                        + MiscellaneousTravelCostVM.TotalRiskCostSellPrice
   *
   * ToDo : SAP_SUBMITTAL — The desktop VM sell prices are derived from costs + GM%.
   * The web EstimateTab has some sell prices directly. When individual sell price breakdowns
   * (LaborCostSellPrice, MiscellaneousCostSellPrice, etc.) become available, implement full calculation.
   * For now, uses: subTotalSellprice - materialCostSellPrice (approximation: total sell minus material = consulting).
   *
   * @param estimateTab The estimate tab data
   * @returns The total consulting labor sell price
   */
  private calculatePSConsultingLabor(estimateTab: EstimateTab | undefined): number {
    if (!estimateTab) {
      return 0;
    }

    // ToDo : SAP_SUBMITTAL — Replace with granular sell prices when available:
    //   return (laborCostSellPrice ?? 0)
    //        + (miscellaneousCostSellPrice ?? 0)
    //        + (subcontractorFeeCostSellPrice ?? 0)
    //        + (mechFeeCostSellPrice ?? 0)
    //        + (subContractorCostSellPrice ?? 0)
    //        + (technologyFeeSellPrice ?? 0)
    //        + (totalRiskCostSellPrice ?? 0);

    // Approximation: total sell price minus material sell price = consulting labor portion
    const totalSellPrice = estimateTab.subTotalSellprice ?? 0;
    const materialSellPrice = estimateTab.materialCostSellPrice ?? 0;
    return totalSellPrice - materialSellPrice;
  }

   async getCostReviewDocUploadModel(): Promise<DocumentUploadModel> {
    const model = new DocumentUploadModel();
    const metaData = this.commonDataService.getEstimateMetaData();
    const userProfile = this.userProfileService.userInfo;
    const costReviewChecklistName = this.commonFunctionsService.getCostReviewChecklistNameBasedOnBusiness();
    
    const fileBytes = await this.pdfBufferService.getReportPdfBytesByReportName(costReviewChecklistName);
    if(!fileBytes){
      return model;
    }
    const base64String = await this.commonFunctionsService.getBase64StringFromBytes(fileBytes);

    model.dataStream = `${CONSTANTS.DATASTREAM_PREFIX}${base64String}`;
    model.documentType = CONSTANTS.COSTREVIEW;
    model.opportunityKey = metaData.estimateSettings.opportunityId ?? '';
    model.modifiedByUser = userProfile?.mail;
    model.name = `${this.commonDataService.getEstimateId()}${CONSTANTS.UNDERSCORE}${CONSTANTS.COSTREVIEW}${CONSTANTS.FILE_EXT_PDF}`;
    model.title = model.name;
    model.comments = '';
    model.sapJobNumber = '';
    model.opportunityName = '';
    return model;
  }

   async getSubmittalPackageDocUploadModel(): Promise<DocumentUploadModel> {
    const model = new DocumentUploadModel();
    const metaData = this.commonDataService.getEstimateMetaData();
    const userProfile = this.userProfileService.userInfo;
    
    const submittalPackageId = metaData.estimateSettings.submittalPackageId
    || this.commonFunctionsService.getSubmittalPackageFromPreferences() || '';

    if(!submittalPackageId){
      return model;
    }
    
    const reportSequence = this.reportsCommonService.getReportSequence(submittalPackageId);

    if(reportSequence.length === 0){
      return model;
    }

    const fileBytes = await this.pdfBufferService.getMultiReportPdfBytes(reportSequence);
    if(!fileBytes){
      return model;
    }
    const base64String = await this.commonFunctionsService.getBase64StringFromBytes(fileBytes);

    model.dataStream = `${CONSTANTS.DATASTREAM_PREFIX}${base64String}`;
    model.documentType = CONSTANTS.SUBMITTAL;
    model.opportunityKey = metaData.estimateSettings.opportunityId ?? '';
    model.modifiedByUser = userProfile?.mail;
    model.name = `${this.commonDataService.getEstimateId()}${CONSTANTS.UNDERSCORE}${CONSTANTS.SUBMITTAL}${CONSTANTS.FILE_EXT_PDF}`;
    model.title = model.name;
    model.comments = '';
    model.sapJobNumber = '';
    model.opportunityName = '';
    return model;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // VALIDATION — HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Parses the summary estimate parameters from the API response into a typed object.
   * These parameters come from WSSP_GetSummaryEstimateData and define the thresholds
   * for determining if an estimate is a "summary estimate".
   */
  private parseSummaryEstimateParameters(params: SummaryEstimateParameter[]): {
    system: number;
    userDefinedParts: number;
    subcontractors: number;
    miscellaneous: number;
    sellPrice: number;
    laborTasks: number;
  } {
    const getParam = (name: string): number => {
      const param = params.find(p => p.parameter === name);
      return param ? parseInt(param.parameterValue, 10) || 0 : 0;
    };

    return {
      system: getParam(SUMMARY_PARAM_SYSTEM),
      userDefinedParts: getParam(SUMMARY_PARAM_USER_DEFINED_PARTS),
      subcontractors: getParam(SUMMARY_PARAM_SUBCONTRACTORS),
      miscellaneous: getParam(SUMMARY_PARAM_MISCELLANEOUS),
      sellPrice: getParam(SUMMARY_PARAM_SELL_PRICE),
      laborTasks: getParam(SUMMARY_PARAM_LABOR_TASKS)
    };
  }

  /**
   * Checks if the project or any of its areas/systems have subcontractor costs.
   * Maps to desktop: counting SubcontractorCollection.Count at each level.
   * In the web model, we use the aggregated subcontractorCategory.sumUpCost as a proxy.
   */
  private hasSubcontractorCosts(sqWebProject: SQWebProject): boolean {
    // Project-level subcontractors
    if (sqWebProject.subcontractorCategory?.sumUpCost > 0) {
      return true;
    }

    // Check all areas recursively
    if (sqWebProject.areas) {
      for (const area of sqWebProject.areas) {
        if (this.areaHasSubcontractorCosts(area)) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Recursively checks if an area or its children have subcontractor costs.
   */
  private areaHasSubcontractorCosts(area: SQWebArea): boolean {
    if (area.subcontractorCategory?.sumUpCost > 0) {
      return true;
    }

    if (area.children) {
      for (const child of area.children) {
        const areaChild = child as IAreaChild;
        if (areaChild.sqWebProjectComponentNodeType === 'SQWebArea') {
          if (this.areaHasSubcontractorCosts(child as SQWebArea)) {
            return true;
          }
        } else {
          // System or other component
          const system = child as SQWebSystem;
          if (system.subcontractorCategory?.sumUpCost > 0) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Checks if the estimate has any non-zero labor hours.
   * Maps to desktop: GetLaborTasksCount(Tasks) checking PM/Eng/Mech/Elec/Specialist > 0.
   * In the web model, we check the estimate tab's labor hour fields.
   */
  private hasNonZeroLaborHours(estimateTab: EstimateTab): boolean {
    return (
      (estimateTab.projectManagementLaborHour ?? 0) > 0 ||
      (estimateTab.engineeringLaborHour ?? 0) > 0 ||
      (estimateTab.networkEngineeringLaborHour ?? 0) > 0 ||
      (estimateTab.qualificationEngineeringLaborHour ?? 0) > 0 ||
      (estimateTab.cyberSecEngineeringLaborHour ?? 0) > 0 ||
      (estimateTab.energyEngineeringLaborHour ?? 0) > 0 ||
      (estimateTab.specialistLaborHour ?? 0) > 0 ||
      (estimateTab.mechanicalLaborHour ?? 0) > 0 ||
      (estimateTab.electricianLaborHour ?? 0) > 0 ||
      (estimateTab.programmingLaborHour ?? 0) > 0 ||
      (estimateTab.graphicsLaborHour ?? 0) > 0 ||
      (estimateTab.digitalDeploymentLaborHour ?? 0) > 0
    );
  }

  /**
   * Counts visible systems within an area (recursively for nested areas).
   * Maps to desktop: counting systems where IsItemVisible == true.
   */
  private countVisibleSystems(area: SQWebArea): number {
    let count = 0;

    if (area.children) {
      for (const child of area.children) {
        if (!child.isItemVisible) {
          continue;
        }

        const areaChild = child as IAreaChild;
        if (areaChild.sqWebProjectComponentNodeType === 'SQWebArea') {
          // Nested area - recurse (maps to desktop SummaryEstimatecheck method)
          count += this.countVisibleSystems(child as SQWebArea);
        } else {
          // System - count it
          count++;
        }
      }
    }

    return count;
  }

  /**
   * Counts user-defined materials and checks for standard equipment presence across all systems.
   *
   * Desktop equivalent: GetCountFromProjectComponent() → GetCountOfMaterials() in MenuViewModel.cs
   *
   * Material counting rules (from desktop GetCountOfMaterials):
   *   - A Material with non-empty Guid OR ReferenceNumber == USER_DEFINED_RPN → counted as user-defined material
   *   - A Material that doesn't match the above → indicates standard equipment is present
   *   - SubSystem children are recursed into
   *
   * @param materialLevelwiseData All material data from IndexedDB, keyed by system ID
   * @returns materialCount: number of user-defined materials; hasStandardEquipment: whether any standard equipment exists
   */
  private countMaterialsAndCheckStandardEquipment(materialLevelwiseData: MaterialLevelwiseData): {
    materialCount: number;
    hasStandardEquipment: boolean;
  } {
    let materialCount = 0;
    let hasStandardEquipment = false;

    if (!materialLevelwiseData?.materialsCollection) {
      return { materialCount, hasStandardEquipment };
    }

    // Iterate over all systems' material data
    for (const systemId of Object.keys(materialLevelwiseData.materialsCollection)) {
      const materialData = materialLevelwiseData.materialsCollection[systemId];
      if (!materialData?.systemChildrenCollection) {
        continue;
      }

      const result = this.countMaterialsInChildren(materialData.systemChildrenCollection);
      materialCount += result.count;
      if (result.hasStandard) {
        hasStandardEquipment = true;
      }
    }

    return { materialCount, hasStandardEquipment };
  }

  /**
   * Recursively counts user-defined materials in a SystemChildren collection.
   *
   * Desktop equivalent: GetCountOfMaterials() in MenuViewModel.cs
   *   - If SubSystem → recurse into Children
   *   - If Material: Guid non-empty OR rpn == USER_DEFINED_RPN → count++
   *   - Otherwise → isSummaryEstimateStandardEquipment = true
   */
  private countMaterialsInChildren(children: SystemChildren): { count: number; hasStandard: boolean } {
    let count = 0;
    let hasStandard = false;

    if (!children) {
      return { count, hasStandard };
    }

    for (const child of children) {
      const nodeType = child.systemComponentNodeType ?? '';

      if (nodeType === 'SubSystem') {
        // Recurse into subsystem children (desktop: SubSystem → Children)
        const subSystem = child as SubSystem;
        if (subSystem.children) {
          const result = this.countMaterialsInChildren(subSystem.children);
          count += result.count;
          if (result.hasStandard) {
            hasStandard = true;
          }
        }
      } else if (nodeType === 'Material') {
        // Desktop: if (!string.IsNullOrEmpty(materialToAdd.Guid) || material.ReferenceNumber == USER_DEFINED_RPN)
        const material = child as Material;
        if ((material.guid && material.guid.trim().length > 0) ||
            material.rpn === CONSTANTS.USER_DEFINED_REFERENCE_NUMBER) {
          count++;
        } else {
          // Non-user-defined material = standard equipment
          hasStandard = true;
        }
      }
    }

    return { count, hasStandard };
  }
}
